import { forwardRef, Ref, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Case, CaseDiagnosis, CaseReport, User } from "pwapi";

import { requestSignout } from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import {
  allSlidesViewedOnCase,
  allSpecimenDiagnosedOnCase,
  hasAnyDiagnosticInformation,
  validityOfDiagnosis,
} from "./DiagnosisDialogHelpers";
import LocationCodeSelector from "./LocationCodeSelector";
import {
  invalidCaseReasons,
  isModifyingReport,
  softDiagnosisValidationReasons,
} from "./utils";
import { SignoutAlertModal } from "../modals/AlertModal";

interface SignOutButtonProps {
  caseDetailObject: CaseDetailState;
  currentSlideId?: number;
  label?: string;
  user?: User;
  selectedSignatureId?: number;
  formErrors?: boolean;
  className?: string;
  showSpinner?: boolean;
  isPrelim?: boolean;
}

const SignOutButton = forwardRef(
  (props: SignOutButtonProps, ref: Ref<HTMLButtonElement>) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
      caseDetailObject,
      currentSlideId,
      label,
      user,
      selectedSignatureId,
      formErrors,
      className,
      showSpinner,
      isPrelim,
    } = props;
    const [signoutAlert, setSignoutAlert] = useState(false);
    const [signingOut, setSigningOut] = useState<boolean>(false);

    const features = useSelector(({ sys }: ApplicationState) => sys.features);

    const locationCodes = useSelector(
      ({ auth }: ApplicationState) => auth.user?.location_codes
    );
    const locationCodeSelect = useRef<HTMLSelectElement>(null);

    const isModifyingRep = isModifyingReport(caseDetailObject, features, user);
    const userSignatures =
      caseDetailObject.source === Case.source.PW
        ? useSelector(
            ({ userSignatures }: ApplicationState) =>
              userSignatures?.[
                isModifyingRep?.source === CaseDiagnosis.source.CORRECTION
                  ? caseDetailObject.report_signed_signature?.user_id || 0
                  : user?.id || 0
              ]
          )
        : undefined;

    // Take the selected signature, if it exists.
    // Otherwise, drop back to previous behaviour
    // This is for backwards-compatibility for the group of cases that won't have a
    // finalizing_signature_id when we first roll this out
    // TODO: @James feels like a lot of duplication between here and CaseReport.tsx
    const defaultSignature =
      userSignatures?.find(
        (s) => s.id === caseDetailObject.finalizing_signature_id
      ) || userSignatures?.[0];

    const allSlidesViewed = allSlidesViewedOnCase(
      caseDetailObject,
      currentSlideId
    );

    const allAreDiagnosed =
      isPrelim || allSpecimenDiagnosedOnCase(caseDetailObject);

    const skipEnterKeyUnlessShift = (e: React.KeyboardEvent<HTMLElement>) => {
      // Disable Enter key unless Shift is held
      const enter = e.key === "Enter" || e.code === "Enter" || e.keyCode === 13;
      if (enter && !e.shiftKey) e.preventDefault();
    };

    const skipSpaceKey = (e: React.KeyboardEvent<HTMLElement>) => {
      // Disable Space Key
      const space = e.code === "Space";
      if (space) e.preventDefault();
    };

    const caseDiagnoses = caseDetailObject.specimenDetail
      .map((s) => {
        const specimensDiagnosis = s.diagnoses?.filter(
          (d) => d.user?.id === user?.id && d.is_completed === false
        );
        return specimensDiagnosis && specimensDiagnosis.length
          ? specimensDiagnosis[0]
          : undefined;
      })
      .filter((d) => d);

    const hasAnyDiagnosis = useMemo(() => {
      return !!caseDiagnoses?.find((d) => d && hasAnyDiagnosticInformation(d));
    }, [caseDiagnoses]);

    const invalidDiagnoses = useMemo(() => {
      return isPrelim
        ? []
        : caseDiagnoses
            ?.map((d) => d && validityOfDiagnosis(d))
            .filter((d) => d && !d.valid) || [];
    }, [caseDiagnoses]);

    const missingSignatures =
      !caseDetailObject.is_screening &&
      caseDetailObject.source === Case.source.PW &&
      !(selectedSignatureId || (userSignatures && userSignatures.length > 0));

    const buttonIsDisabled =
      (isModifyingReport(caseDetailObject, features, user)?.source !==
        CaseDiagnosis.source.CORRECTION &&
        !isPrelim &&
        !allSlidesViewed) ||
      (isPrelim && !hasAnyDiagnosis) ||
      !allAreDiagnosed ||
      !!invalidDiagnoses.length ||
      caseDetailObject.diagnosis_status === "loading" ||
      formErrors ||
      signingOut ||
      missingSignatures;

    const buttonTooltip = [
      isModifyingReport(caseDetailObject, features, user)?.source !==
        CaseDiagnosis.source.CORRECTION &&
        !isPrelim &&
        !allSlidesViewed &&
        "Unviewed Slides",
      !allAreDiagnosed && "Undiagnosed Specimen",
      ...(isPrelim && !hasAnyDiagnosis
        ? ["Missing any diagnostic information on any specimen"]
        : []),
      !!invalidDiagnoses.length &&
        invalidDiagnoses
          .map(
            (v) =>
              `Specimen ${
                caseDetailObject.specimenDetail.find(
                  (s) => s.id === v?.specimen
                )?.name
              } missing ${v?.missing.join(", ")}`
          )
          .join("\n"),
      missingSignatures && "Missing Signature",
    ]
      .filter((v) => !!v)
      .join(", ");

    const invalidReasons = invalidCaseReasons(caseDetailObject, features);
    const softDiagnosisValidations = softDiagnosisValidationReasons(
      caseDetailObject,
      caseDiagnoses
    );

    const signOutCase = () => {
      if (!buttonIsDisabled) {
        const modifyingReport = isModifyingReport(
          caseDetailObject,
          features,
          user
        );
        // Use the *value that is in the actual dropdown*
        // ... So we're not playing games with localStorage or store state
        // ... The value of the input is the user's intention.
        const locationCodeId = locationCodeSelect.current?.value
          ? !Number.isNaN(locationCodeSelect.current?.value)
            ? Number(locationCodeSelect.current?.value)
            : undefined
          : undefined;
        caseDetailObject?.id &&
          dispatch(
            requestSignout(
              caseDetailObject.id,
              navigate,
              caseDetailObject.source === Case.source.PW
                ? selectedSignatureId || defaultSignature?.id
                : undefined,
              isPrelim
                ? CaseReport.report_type.PRELIMINARY
                : modifyingReport?.source
                ? modifyingReport?.source === CaseDiagnosis.source.AMEND
                  ? CaseReport.report_type.AMEND
                  : modifyingReport?.source === CaseDiagnosis.source.ADDEND
                  ? CaseReport.report_type.ADDEND
                  : CaseReport.report_type.CORRECTION
                : caseDetailObject.is_screening
                ? CaseReport.report_type.SCREEN
                : CaseReport.report_type.FINALIZING,
              isPrelim ? undefined : locationCodeId
            )
          );
        setSigningOut(true);
      }
    };

    const onHandleSignOut = (e: React.FormEvent) => {
      e.preventDefault();
      if (
        !isPrelim &&
        (invalidReasons?.length ||
          softDiagnosisValidations?.length ||
          caseDetailObject.status === Case.status.WORKING)
      ) {
        setSignoutAlert(true);
      } else if (
        (isPrelim && caseDetailObject.status === Case.status.WORKING) ||
        caseDetailObject.status === Case.status.ACTIVE ||
        caseDetailObject.status === Case.status.FINALIZED
      ) {
        signOutCase();
      }
    };

    return (
      <>
        {caseDetailObject.source === Case.source.PW &&
          !caseDetailObject.is_screening &&
          !isPrelim &&
          !!locationCodes?.length && (
            <LocationCodeSelector ref={locationCodeSelect} />
          )}
        <button
          className={className}
          type="button"
          disabled={buttonIsDisabled}
          onKeyDown={skipEnterKeyUnlessShift}
          onKeyUp={skipSpaceKey}
          onClick={onHandleSignOut}
          title={buttonTooltip || undefined}
          ref={ref}
        >
          {showSpinner && caseDetailObject.diagnosis_status === "loading" && (
            <Spinner
              size={"16px"}
              width={"3px"}
              backColor={"var(--blue15)"}
              frontColor={"var(--blue)"}
            />
          )}
          {label ||
            (isPrelim && "Issue Preliminary Report") ||
            (caseDetailObject.is_screening && "Screen") ||
            "Sign Out"}
        </button>
        {(caseDetailObject.status === Case.status.WORKING ||
          (invalidReasons &&
            Array.isArray(invalidReasons) &&
            invalidReasons.length !== 0) ||
          (softDiagnosisValidations &&
            Array.isArray(softDiagnosisValidations) &&
            softDiagnosisValidations.length !== 0)) &&
          !isPrelim && (
            <SignoutAlertModal
              {...{
                signOutCase,
                signoutAlert,
                setSignoutAlert,
                caseDetailObject,
                invalidReasons,
                softDiagnosisValidations,
              }}
              currentUser={user}
            />
          )}
      </>
    );
  }
);

export default SignOutButton;
